/**
 * Composable for handling password reset functionality.
 * Provides methods to send a password reset email through Auth0.
 *
 * @returns {Object} An object containing password reset methods and state
 * @property {Ref<boolean>} emailSent - Indicates whether a password reset email has been sent
 * @property {Function} sendEmail - Method to request a password reset email
 */
export const useResetPassword = () => {
  /**
   * Reactive reference to track email sending status.
   * @type {Ref<boolean>}
   */
  const emailSent = ref(false)

  /**
   * Sends a password reset email to the specified email address.
   *
   * @async
   * @param {string} email - The email address to send the password reset link to
   * @param {string} currentDomain - The current domain context for the password reset
   * @throws {Error} Logs any errors that occur during the password reset request
   * @example
   * const { sendEmail } = useResetPassword()
   * await sendEmail('user@example.com', 'https://example.com')
   */
  const sendEmail = async (email: string, currentDomain: string) => {
    try {
      await $fetch('/api/auth0/requestPasswordChange', {
        method: 'POST',
        body: {
          email,
          currentDomain,
        },
      })
      emailSent.value = true
    } catch (error) {
      console.error(error)
    }
  }

  return {
    emailSent,
    sendEmail,
  }
}
